import React from 'react'

import { Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'

/**
 * A labelled checkbox row — the house treatment, extracted (§36) from the modals that
 * each wrote it inline (CreateAddressModal's "Save as default address", AssignUsersModal's
 * list): the THEMED MUI `Checkbox` at `size="small"` with no padding and an 8px gap, and
 * the label on the `body3` field-label size, not MUI's default body1.
 *
 * The theme owns the box itself (`packages/ui/theme/checkboxIcon.tsx` + the MuiCheckbox
 * overrides), so this adds only the row: label, an optional one-line description under it
 * on `body4` / `text.secondary` (the helper-text treatment fields use), and the inset that
 * keeps that description aligned with the label rather than the box.
 *
 * `@repo/ui`'s `CheckBox` is NOT this: it renders the label at MUI's default size inside a
 * full-width FormControl, which is what made the People invite form's options read as
 * headings (Spencer, 2026-10-01).
 */
export const CheckboxRow = ({
  label,
  description,
  checked,
  onChange,
  disabled,
  indent = false,
}: {
  label: React.ReactNode
  description?: React.ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  /** Nest under the row above (a sub-option), aligned with its label. */
  indent?: boolean
}) => (
  // The indent is PADDING on a wrapper, not margin on FormControlLabel: MUI gives that
  // label a negative left margin of its own, which swallowed a margin indent entirely.
  // 26px = the small box (18) + its 8px gap, so a sub-option's box sits under the
  // parent's label.
  <Box sx={{ pl: indent ? '26px' : 0 }}>
  <FormControlLabel
    disabled={disabled}
    sx={{ ml: 0, mr: 0, alignItems: 'flex-start' }}
    control={
      <Checkbox
        size="small"
        sx={{ p: 0, mr: 2, mt: '2px' }}
        checked={checked}
        onChange={(_, value) => onChange(value)}
      />
    }
    label={
      <Stack spacing={0.5}>
        <Typography variant="body3">{label}</Typography>
        {description && (
          <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {description}
          </Typography>
        )}
      </Stack>
    }
  />
  </Box>
)
